/**
 * Per-field data quality (spec section 56).
 *
 * Jeabs returns `0` both for a genuine zero and for "we have not enriched this
 * field yet". `PlayerSummary.quality` records which, and this component is the
 * only sanctioned way to render a field that carries a quality flag — so a
 * POSSIBLE/UNKNOWN value can never be painted identically to a CONFIRMED one.
 *
 * The visual language, applied consistently across the app:
 *   CONFIRMED  plain text, no decoration
 *   POSSIBLE   dashed amber underline + "?"  — the value may be a placeholder
 *   UNKNOWN    dashed amber underline + "?"  — we cannot interpret it at all
 *   STALE      dashed orange underline + "~" — real, but measured a while ago
 *
 * The decoration carries the same meaning in both locales, which matters: the
 * flag is what an operator scanning a dense table actually reads, not the
 * tooltip behind it.
 */

import type { PlayerSummary } from '@kso/schemas';
import { useT } from '../../i18n';
import type { Dictionary } from '../../i18n';

type DataQuality = PlayerSummary['quality'][string];

const FLAG: Record<DataQuality, string> = {
  CONFIRMED: '',
  POSSIBLE: '?',
  UNKNOWN: '?',
  STALE: '~',
};

function explain(t: Dictionary, quality: DataQuality, field: string): string {
  switch (quality) {
    case 'CONFIRMED':
      return t.quality.confirmed(field);
    case 'POSSIBLE':
      return t.quality.possible(field);
    case 'UNKNOWN':
      return t.quality.unknown(field);
    case 'STALE':
      return t.quality.stale(field);
    default:
      return field;
  }
}

/**
 * @param quality The field's entry from the response's `quality` record. When
 *   the server did not report one, treat it as UNKNOWN rather than assuming
 *   the value is good.
 */
export function Qualified({
  field,
  quality,
  value,
  absent,
}: {
  field: string;
  quality: DataQuality | undefined;
  value: string | number | null | undefined;
  absent?: string;
}) {
  const t = useT();

  if (value === null || value === undefined || value === '') {
    return (
      <span className="absent" title={t.quality.neverRecorded(field)}>
        {absent ?? t.common.unknown}
      </span>
    );
  }

  const resolved: DataQuality = quality ?? 'UNKNOWN';
  if (resolved === 'CONFIRMED') return <span>{value}</span>;

  return (
    <span className={`qualified qualified--${resolved}`} title={explain(t, resolved, field)}>
      {value}
      <span className="qualified__flag" aria-label={t.quality.flagAria(resolved)}>
        {FLAG[resolved]}
      </span>
    </span>
  );
}

/**
 * A short caption naming how many fields on this record are unconfirmed.
 * Cheap to render and it stops a table of dashed underlines from looking like
 * a rendering glitch.
 */
export function QualitySummary({ quality }: { quality: PlayerSummary['quality'] }) {
  const t = useT();
  const entries = Object.entries(quality);
  const unconfirmed = entries.filter(([, value]) => value !== 'CONFIRMED');

  if (entries.length === 0) {
    return <span className="faint small">{t.quality.noneReported}</span>;
  }
  if (unconfirmed.length === 0) {
    return <span className="faint small">{t.quality.allConfirmed(entries.length)}</span>;
  }
  return (
    <span
      className="faint small"
      title={unconfirmed.map(([key, value]) => `${key}: ${value}`).join('\n')}
    >
      {t.quality.unconfirmedCount(unconfirmed.length, entries.length)}
    </span>
  );
}
